import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Megaphone, Trash2, Plus, AlertCircle, CheckCircle2 } from "lucide-react";

export default function AnnouncementsManager() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [link, setLink] = useState("/news");
  const [priority, setPriority] = useState(5);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setLoading(true);
    try {
      await api.post("/announcements", { text, link, priority });
      setText(""); setLink("/news"); setPriority(5);
      setOk("Announcement added — will appear in the top strip within 30 seconds.");
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this announcement?")) return;
    try { await api.delete(`/announcements/${id}`); refresh(); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="announcements-manager">
      <div className="flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Top-Strip Announcements</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Anything added below appears live in the top federation marquee within ~30 seconds.
      </p>

      <form onSubmit={add} className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <label className="md:col-span-7 block">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Announcement Text</span>
          <input required value={text} onChange={(e) => setText(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" placeholder="e.g. Annual Planner 2026 has been released" data-testid="ann-text" />
        </label>
        <label className="md:col-span-3 block">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Link (optional)</span>
          <input value={link} onChange={(e) => setLink(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" placeholder="/news" data-testid="ann-link" />
        </label>
        <label className="md:col-span-1 block">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Priority</span>
          <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" data-testid="ann-priority" />
        </label>
        <div className="md:col-span-1 flex justify-end">
          <button disabled={loading} className="bg-nkf-blue text-white px-4 py-2.5 rounded text-sm font-bold uppercase tracking-widest hover:bg-nkf-navy transition inline-flex items-center gap-1 disabled:opacity-60" data-testid="ann-add">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </form>

      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
      {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}

      <ul className="mt-5 divide-y divide-slate-100 border border-slate-100 rounded-md">
        {items.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-[11px] font-bold tabular-nums text-nkf-gold w-5 text-right">{a.priority}</span>
              <span className="text-nkf-blue font-semibold truncate">{a.text}</span>
              {a.link && <span className="text-[11px] text-slate-400 truncate hidden md:inline">{a.link}</span>}
            </div>
            <button onClick={() => remove(a.id)} data-testid={`ann-del-${a.id}`} className="text-red-600 hover:text-red-800 transition">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
        {!items.length && <li className="px-3 py-6 text-center text-slate-400 text-sm">No announcements yet.</li>}
      </ul>
    </section>
  );
}
